import * as React from "react";
import { Check, ChevronDown } from "lucide-react";
import { cn } from "@/lib/utils";
import { buttonVariants } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
} from "@/components/ui/command";

type SourceTypeOption = {
  value: string;
  label: string;
};

type SourceTypeMultiSelectProps = {
  options: ReadonlyArray<SourceTypeOption>;
  selected: string[];
  onChange: (value: string[]) => void;
  children?: React.ReactNode;
  className?: string;
  allValue?: string;
  ariaLabel?: string;
  renderOptionLabel?: (option: SourceTypeOption) => React.ReactNode;
};

const dedupe = (values: string[]) => Array.from(new Set(values));

const normalizeSelection = (
  selected: string[],
  options: ReadonlyArray<SourceTypeOption>,
  allValue: string,
) => {
  const allowedValues = new Set(options.map((option) => option.value));
  const filtered = dedupe((selected || []).filter((value) => allowedValues.has(value)));
  if (!filtered.length) return [allValue];
  if (filtered.includes(allValue) && filtered.length > 1) {
    return filtered.filter((value) => value !== allValue);
  }
  return filtered;
};

export function SourceTypeMultiSelect({
  options,
  selected,
  onChange,
  children,
  className,
  allValue = "all",
  ariaLabel = "Filter by source type",
  renderOptionLabel,
}: SourceTypeMultiSelectProps) {
  const [open, setOpen] = React.useState(false);
  const normalizedSelected = React.useMemo(
    () => normalizeSelection(selected, options, allValue),
    [allValue, options, selected],
  );
  const selectedSpecific = normalizedSelected.filter((value) => value !== allValue);
  const isAllSelected = selectedSpecific.length === 0;
  const optionsByValue = React.useMemo(
    () => new Map(options.map((option) => [option.value, option])),
    [options],
  );
  const allOption = optionsByValue.get(allValue);

  const summaryLabel = React.useMemo(() => {
    if (isAllSelected) {
      return allOption?.label || "All types";
    }
    const firstSelected = selectedSpecific[0];
    const firstLabel = optionsByValue.get(firstSelected)?.label || firstSelected;
    if (selectedSpecific.length === 1) return firstLabel;
    return `${firstLabel} +${selectedSpecific.length - 1}`;
  }, [allOption?.label, isAllSelected, optionsByValue, selectedSpecific]);

  const handleSelect = (value: string) => {
    if (value === allValue) {
      onChange([allValue]);
      return;
    }
    const isSelected = selectedSpecific.includes(value);
    const next = isSelected
      ? selectedSpecific.filter((item) => item !== value)
      : [...selectedSpecific, value];
    onChange(next.length ? next : [allValue]);
  };

  const specificOptions = options.filter((option) => option.value !== allValue);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button
          type="button"
          role="combobox"
          aria-label={ariaLabel}
          aria-expanded={open}
          className={cn(
            buttonVariants({ variant: "outline" }),
            "h-9 w-fit max-w-[180px] cursor-pointer justify-between px-3 py-2 hover:bg-accent hover:text-accent-foreground",
            className,
          )}
        >
          <span className="flex min-w-0 items-center gap-2">
            {children}
            <span className="truncate text-[#0A0A0A]">{summaryLabel}</span>
          </span>
          <ChevronDown
            className={cn("h-4 w-4 shrink-0 opacity-50 transition-transform", open && "rotate-180")}
          />
        </button>
      </PopoverTrigger>
      <PopoverContent className="w-[210px] rounded-xl p-0">
        <Command>
          <CommandList>
            <CommandGroup>
              <CommandItem
                onSelect={() => handleSelect(allValue)}
                className="cursor-pointer rounded-none px-2 py-2"
              >
                <span className="flex w-full items-center justify-between">
                  <span className="text-sm text-[#0A0A0A]">{allOption?.label || "All types"}</span>
                  {isAllSelected ? <Check className="h-4 w-4" /> : null}
                </span>
              </CommandItem>
              <CommandSeparator className="mx-2" />
              {specificOptions.map((option) => {
                const selectedNow = selectedSpecific.includes(option.value);
                return (
                  <CommandItem
                    key={option.value}
                    onSelect={() => handleSelect(option.value)}
                    className="cursor-pointer px-2 py-2"
                  >
                    <span className="flex w-full items-center justify-between gap-2">
                      <span className="min-w-0">
                        {renderOptionLabel ? renderOptionLabel(option) : option.label}
                      </span>
                      {selectedNow ? <Check className="h-4 w-4 shrink-0" /> : null}
                    </span>
                  </CommandItem>
                );
              })}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}
